"use client";

import { useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "motion/react";
import { ChevronDown } from "lucide-react";

/**
 * A section of a page with a heading that folds. `open` is where it starts;
 * `foldable` false draws the heading with no chevron and keeps it open.
 * `footer` stays under the heading whether it is folded or not, for what must
 * not be hidden with the rest. A section with nothing to show is not drawn at
 * all -- the caller decides.
 */
export function Section({
  title,
  open = true,
  foldable = true,
  children,
  id,
  trailing,
  footer,
  keep = false,
  className = "",
}: {
  title: ReactNode;
  open?: boolean;
  foldable?: boolean;
  children: ReactNode;
  id?: string;
  trailing?: ReactNode;
  footer?: ReactNode;
  /** the body stays in the page while folded, out of sight: for text a search engine should read; never for a map */
  keep?: boolean;
  className?: string;
}) {
  const [shown, setShown] = useState(open);
  const isOpen = foldable ? shown : true;

  return (
    <section id={id} className={`scroll-mt-16 border-t border-line py-4 first:border-t-0 desk:scroll-mt-32 ${className}`}>
      <button
        type="button"
        className={`flex w-full items-center gap-2 text-left ${foldable ? "" : "cursor-default"}`}
        onClick={() => foldable && setShown((v) => !v)}
        aria-expanded={isOpen}
      >
        <h2 className="min-w-0 flex-1 text-[17px] font-bold">{title}</h2>
        {trailing}
        {foldable && (
          <motion.span animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }} className="text-ink-mute">
            <ChevronDown size={20} />
          </motion.span>
        )}
      </button>
      {keep && (
        <motion.div
          initial={false}
          animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
          transition={{ duration: 0.24, ease: [0.2, 0.8, 0.2, 1] }}
          className="overflow-hidden"
          inert={!isOpen}
        >
          <div className="pt-3">{children}</div>
        </motion.div>
      )}
      <AnimatePresence initial={false}>
        {!keep && isOpen && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.24, ease: [0.2, 0.8, 0.2, 1] }}
            className="overflow-hidden"
          >
            <div className="pt-3">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
      {footer && <div className="pt-3">{footer}</div>}
    </section>
  );
}

/** a card holding a section's content on a phone page: white, rounded, padded */
export function Card({ children, className = "", padded = true }: { children: ReactNode; className?: string; padded?: boolean }) {
  return <div className={`card ${padded ? "p-4" : ""} ${className}`}>{children}</div>;
}
